import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Paper, Typography, Alert } from "@mui/material";
import { CalendarDate, today, getLocalTimeZone, startOfMonth } from "@internationalized/date";
import { useDispatch, useSelector } from "react-redux";
import { ApiCall } from "../../helper/axios";
import { toast } from "../../common_components/Toast/ToastUtils";
import PageContainer from "../../common_components/PageContainer";
import LastUpdatedInfo from "../../common_components/LastUpdatedInfo";
import CommonLoader from "../../common_components/CommonLoader";
import CustomMonthComparePicker from "../../common_components/daterangepicker/CustomMonthComparePicker";
import { Dropdown } from "../../common_components/FormInputs";
import IOSSwitch from "../../common_components/IOSSwitch";
import ChannelTabs from "../../common_components/ChannelTabs";
import DailyUpdateTab from "./components/DailyUpdateTab";
import SummaryTab from "./components/SummaryTab";
import PacerTab from "./components/PacerTab";
import { fetchCommissionDailyUpdate, fetchCommissionSummary, fetchCommissionPacer, selectCommissionDailyUpdate, selectCommissionDailyUpdateLoading, selectCommissionDailyUpdateError, selectCommissionLastUpdatedAt, selectCommissionSummary, selectCommissionSummaryLoading, selectCommissionSummaryError, selectCommissionPacer, selectCommissionPacerLoading, selectCommissionPacerError, } from "../../redux/commissionTrackerSlice";
import { mapDailyUpdateRows, mapDailyUpdateTotals, mapSummaryRows, mapSummaryTotals, } from "./commissionTracker.mappers";
import { fetchDesignationWiseUsers, selectDesignationWiseUsers } from "../../redux/clientDataSlice";
import { selectCommissionHasPacerAccess, selectCommissionCanViewReport, selectCommissionHasPaidMediaAccess, selectCommissionHasAffiliateAccess, } from "../../redux/authSlice";
import { AM_DESIGNATIONS, collectUsersByDesignations } from "../../utils/designationFilters";
import { truncateName } from "../../helper/truncateName";
// year_month ("YYYY-MM") from a CalendarDate.
const toYearMonth = (d) => d ? `${d.year}-${String(d.month).padStart(2, "0")}` : "";
// The applied month, stored as "YYYY-MM" so the selection survives a reload —
// same behaviour as the other month-scoped modules (e.g. Billing Summary).
const MONTH_STORAGE_KEY = "commission_tracker_month";
// The month the user last applied, falling back to the current month when nothing
// is stored, the value is unreadable (private mode) or it isn't a valid "YYYY-MM".
const readStoredMonth = () => {
    const currentMonth = startOfMonth(today(getLocalTimeZone()));
    try {
        const raw = localStorage.getItem(MONTH_STORAGE_KEY);
        const parsed = raw && /^(\d{4})-(\d{2})$/.exec(raw);
        if (!parsed)
            return currentMonth;
        const year = Number(parsed[1]);
        const month = Number(parsed[2]);
        if (month < 1 || month > 12)
            return currentMonth;
        return new CalendarDate(year, month, 1);
    }
    catch {
        return currentMonth;
    }
};
// Month-comparison is OFF by default and SHARED by Summary and Pacer — enabling it on
// one tab has it applied on the other, so the same two months are being compared
// wherever the user looks. Persisted here (as "YYYY-MM") so the comparison stays on
// across reloads; an absent / invalid value means comparison is off.
const COMPARE_STORAGE_KEY = "commission_tracker_compare_month";
const readStoredCompareMonth = () => {
    try {
        const raw = localStorage.getItem(COMPARE_STORAGE_KEY);
        const m = raw && /^(\d{4})-(\d{2})$/.exec(raw);
        if (!m)
            return null;
        const year = Number(m[1]);
        const month = Number(m[2]);
        if (month < 1 || month > 12)
            return null;
        return new CalendarDate(year, month, 1);
    }
    catch {
        return null;
    }
};
// Type filter — "All" first, then the remaining channel types A→Z.
const TYPE_OPTIONS = [
    { value: "all", label: "All" },
    ...[
        { value: "affiliate", label: "Affiliate" },
        { value: "paid_media", label: "Paid Media" },
    ].sort((a, b) => a.label.localeCompare(b.label)),
];
// ── Tabs ──────────────────────────────────────────────────────
const TABS = [
    { key: "daily", label: "Daily Update" },
    { key: "summary", label: "Summary" },
    { key: "pacer", label: "Pacer" },
];
export default function CommissionTracker() {
    const dispatch = useDispatch();
    // Pacer tab needs "Pacer Access". The client name links to the report only
    // with "Full Access". (visibleTabs is computed after the month state below.)
    const canViewPacer = useSelector(selectCommissionHasPacerAccess);
    const canOpenReport = useSelector(selectCommissionCanViewReport);
    // The "Paid Media" / "Affiliate" grants decide the Type filter on all three tabs.
    // Exactly ONE of them means the listing IS that type: the dropdown is dropped and
    // every fetch sends the granted type, so a dual-type client reports only its granted
    // side — the same restriction the report page already applies.
    //
    // Both grants, or neither, leave the user free to choose: neither is not "nothing
    // visible", it's the whole client scope unnarrowed by type.
    const hasPaidMediaAccess = useSelector(selectCommissionHasPaidMediaAccess);
    const hasAffiliateAccess = useSelector(selectCommissionHasAffiliateAccess);
    const grantedType = hasPaidMediaAccess && !hasAffiliateAccess
        ? "paid_media"
        : hasAffiliateAccess && !hasPaidMediaAccess
            ? "affiliate"
            : null;
    const canFilterByType = grantedType === null;
    // ── Filter state ──────────────────────────────────────────
    const [activeTab, setActiveTab] = useState("daily");
    const [commissionClientsOnly, setCommissionClientsOnly] = useState(true);
    const [type, setType] = useState("all");
    // What the tabs actually request: the user's choice only where the dropdown is
    // offered, otherwise the single granted type.
    const effectiveType = grantedType ?? type;
    const [accountManager, setAccountManager] = useState(""); // "" = All
    // ── Account Manager options (same source as Account Performance) ──
    const designationWiseUsers = useSelector(selectDesignationWiseUsers);
    useEffect(() => {
        dispatch(fetchDesignationWiseUsers());
    }, [dispatch]);
    const accountManagers = useMemo(() => collectUsersByDesignations(designationWiseUsers?.designationMap, AM_DESIGNATIONS), [designationWiseUsers]);
    const amOptions = useMemo(() => [
        { value: "", label: "All" },
        ...accountManagers.map((u) => ({ value: u._id, label: `${u.first_name} ${u.last_name}` })),
    ], [accountManagers]);
    const selectedAm = useMemo(() => accountManagers.find((u) => u._id === accountManager), [accountManager, accountManagers]);
    // Month compare picker — primary drives the Daily Update fetch. Restored from
    // localStorage (current month when nothing is stored) so the applied month
    // survives a reload; passed as an initialiser so storage is read once on mount.
    const [primaryMonth, setPrimaryMonth] = useState(readStoredMonth);
    // Comparison is OFF by default; restored from localStorage so an enabled
    // comparison (and the chosen month) survives a page reload. Shared by Summary and
    // Pacer: the month applied on either tab is the month the other one compares
    // against. On Pacer it surfaces the "Compare with prior periods" button below the
    // chart, and clicking that button is what draws the overlay.
    const [compareMonth, setCompareMonth] = useState(readStoredCompareMonth);
    // Remember the applied month for the next visit. Never removes the key — a null
    // month only happens if the picker is cleared, and the stored choice is still the
    // better thing to come back to than resetting to the current month.
    useEffect(() => {
        try {
            if (primaryMonth)
                localStorage.setItem(MONTH_STORAGE_KEY, toYearMonth(primaryMonth));
        }
        catch {
            /* ignore storage errors (e.g. private mode) */
        }
    }, [primaryMonth]);
    // Persist the compare-month selection (or clear it when comparison is turned off)
    // so the choice is restored on the next reload.
    useEffect(() => {
        try {
            if (compareMonth)
                localStorage.setItem(COMPARE_STORAGE_KEY, toYearMonth(compareMonth));
            else
                localStorage.removeItem(COMPARE_STORAGE_KEY);
        }
        catch {
            /* ignore storage errors (e.g. private mode) */
        }
    }, [compareMonth]);
    // Tabs: Pacer needs "Pacer Access"; the Daily Update tab is hidden for past months.
    const isDailyPastMonth = useMemo(() => {
        const ym = toYearMonth(primaryMonth);
        const cur = toYearMonth(startOfMonth(today(getLocalTimeZone())));
        return !!ym && ym < cur;
    }, [primaryMonth]);
    const visibleTabs = useMemo(() => TABS.filter((t) => (t.key !== "pacer" || canViewPacer) && (t.key !== "daily" || !isDailyPastMonth)), [canViewPacer, isDailyPastMonth]);
    // If the active tab isn't visible (no access / hidden for the month), switch to the first available.
    useEffect(() => {
        if (!visibleTabs.some((t) => t.key === activeTab))
            setActiveTab(visibleTabs[0]?.key ?? "summary");
    }, [activeTab, visibleTabs]);
    // ── Daily Update (API-backed) ─────────────────────────────
    const dailyUpdate = useSelector(selectCommissionDailyUpdate);
    const dailyLoading = useSelector(selectCommissionDailyUpdateLoading);
    const dailyError = useSelector(selectCommissionDailyUpdateError);
    const lastUpdatedAt = useSelector(selectCommissionLastUpdatedAt);
    const dailyRows = useMemo(() => mapDailyUpdateRows(dailyUpdate?.summary), [dailyUpdate]);
    const dailyTotals = useMemo(() => mapDailyUpdateTotals(dailyUpdate?.footer ?? null), [dailyUpdate]);
    // Fetch on mount + whenever the Daily Update filters change (dedup identical requests).
    const lastRequestKey = useRef(null);
    const loadDailyUpdate = useCallback(() => {
        const yearMonth = toYearMonth(primaryMonth);
        if (!yearMonth)
            return;
        const payload = {
            year_month: yearMonth,
            type: effectiveType,
            ...(accountManager ? { am_id: accountManager } : {}),
            is_commission_client: commissionClientsOnly,
        };
        const key = JSON.stringify(payload);
        if (lastRequestKey.current === key)
            return;
        lastRequestKey.current = key;
        dispatch(fetchCommissionDailyUpdate(payload));
    }, [dispatch, primaryMonth, effectiveType, accountManager, commissionClientsOnly]);
    useEffect(() => {
        loadDailyUpdate();
    }, [loadDailyUpdate]);
    // ── Summary (API-backed) ──────────────────────────────────
    const summary = useSelector(selectCommissionSummary);
    const summaryLoading = useSelector(selectCommissionSummaryLoading);
    const summaryError = useSelector(selectCommissionSummaryError);
    const summaryRows = useMemo(() => mapSummaryRows(summary?.summary), [summary]);
    const summaryTotals = useMemo(() => mapSummaryTotals(summary?.summaryfooter ?? null), [summary]);
    // Summary supports month comparison — send compare_month when one is picked.
    const lastSummaryKey = useRef(null);
    const loadSummary = useCallback(() => {
        const yearMonth = toYearMonth(primaryMonth);
        if (!yearMonth)
            return;
        const payload = {
            year_month: yearMonth,
            ...(compareMonth ? { compare_month: toYearMonth(compareMonth) } : {}),
            type: effectiveType,
            ...(accountManager ? { am_id: accountManager } : {}),
            is_commission_client: commissionClientsOnly,
        };
        const key = JSON.stringify(payload);
        if (lastSummaryKey.current === key)
            return;
        lastSummaryKey.current = key;
        dispatch(fetchCommissionSummary(payload));
    }, [dispatch, primaryMonth, compareMonth, effectiveType, accountManager, commissionClientsOnly]);
    // Fetch the summary only once the user is on that tab (then keep it in sync).
    useEffect(() => {
        if (activeTab === "summary")
            loadSummary();
    }, [activeTab, loadSummary]);
    // ── Pacer (API-backed) ────────────────────────────────────
    const pacer = useSelector(selectCommissionPacer);
    const pacerLoading = useSelector(selectCommissionPacerLoading);
    const pacerError = useSelector(selectCommissionPacerError);
    const lastPacerKey = useRef(null);
    const loadPacer = useCallback(() => {
        const yearMonth = toYearMonth(primaryMonth);
        if (!yearMonth)
            return;
        const payload = {
            year_month: yearMonth,
            type: effectiveType,
            ...(accountManager ? { am_id: accountManager } : {}),
            // Compare month drives the charts-only overlay; sent only once the user has
            // enabled + applied a compare month, on either tab (null by default).
            ...(compareMonth ? { compare_month: toYearMonth(compareMonth) } : {}),
        };
        const key = JSON.stringify(payload);
        if (lastPacerKey.current === key)
            return;
        lastPacerKey.current = key;
        dispatch(fetchCommissionPacer(payload));
    }, [dispatch, primaryMonth, effectiveType, accountManager, compareMonth]);
    useEffect(() => {
        if (activeTab === "pacer")
            loadPacer();
    }, [activeTab, loadPacer]);
    const handlePacerRefresh = useCallback(() => {
        lastPacerKey.current = null;
        loadPacer();
    }, [loadPacer]);
    // ── Refresh All (portfolio-wide sync for the month, then reload) ──────────
    const token = useSelector((state) => state.auth?.token);
    const [refreshingAll, setRefreshingAll] = useState(false);
    // Timers for the post-refresh auto-reload poll (backfill runs server-side in
    // the background, so we re-fetch a few times until the results land).
    const refreshPollRef = useRef([]);
    // Drop the cached request keys and reload whichever views are on screen.
    const reloadAllViews = useCallback(() => {
        lastRequestKey.current = null;
        lastSummaryKey.current = null;
        lastPacerKey.current = null;
        loadDailyUpdate();
        if (activeTab === "summary")
            loadSummary();
        if (activeTab === "pacer")
            loadPacer();
    }, [loadDailyUpdate, loadSummary, loadPacer, activeTab]);
    const handleRefreshAll = useCallback(async () => {
        const yearMonth = toYearMonth(primaryMonth);
        if (!yearMonth || refreshingAll)
            return;
        // Clear any auto-reload timers still pending from a previous refresh.
        refreshPollRef.current.forEach(clearTimeout);
        refreshPollRef.current = [];
        setRefreshingAll(true);
        try {
            const response = await ApiCall("POST", "/api/commission-tracker/refresh-all", { year_month: yearMonth }, { Authorization: token || "", "x-module-key": "commission_tracker" }, 10 * 60 * 1000);
            const status = response?.status ?? response?.response?.status;
            // The backfill is acknowledged with 202 and runs in the background
            // (~2-3 min); 200 covers a synchronous completion. Both mean success.
            const ok = (status === 200 || status === 202) && (response?.data?.success ?? true);
            if (!ok)
                throw new Error(response?.data?.message || response?.response?.data?.message || "Refresh failed");
            // toast.info(
            //     response?.data?.message ||
            //     "Refresh started — trackers are syncing in the background. Figures will update automatically in a few minutes."
            // );
            toast.info("We're syncing your client commissions in the background. Your figures will update automatically in a few minutes");
            // The server processes asynchronously, so re-fetch a few times over the
            // next few minutes until the freshly-synced figures land (no manual
            // reload needed). Timers are cleared on the next refresh / unmount.
            [60000, 120000, 180000].forEach((delay) => {
                refreshPollRef.current.push(setTimeout(reloadAllViews, delay));
            });
        }
        catch (err) {
            console.error("Commission tracker refresh-all error:", err);
            toast.error(err?.message || "Failed to refresh. Please try again.");
        }
        finally {
            setRefreshingAll(false);
        }
    }, [primaryMonth, refreshingAll, token, reloadAllViews]);
    // Cancel any pending auto-reload timers when the page unmounts.
    useEffect(() => () => {
        refreshPollRef.current.forEach(clearTimeout);
        refreshPollRef.current = [];
    }, []);
    // ── Save a per-date Pacer note (shared across all clients for that date) ──
    const handleSavePacerNote = useCallback(async (date, notes) => {
        if (!date)
            return;
        try {
            const response = await ApiCall("POST", "/api/commission-tracker/pacer-note", { year_month: toYearMonth(primaryMonth), date, notes }, { Authorization: token || "", "x-module-key": "commission_tracker" });
            const status = response?.status ?? response?.response?.status;
            const ok = status === 200 && (response?.data?.success ?? true);
            if (!ok)
                throw new Error(response?.data?.message || response?.response?.data?.message || "Save failed");
        }
        catch (err) {
            console.error("Save pacer note error:", err);
            toast.error(err?.message || "Failed to save the note. Please try again.");
        }
    }, [primaryMonth, token]);
    // ── Export the current tab's table (POST → CSV blob download) ─────────────
    // Uses the same filters the tab is showing, so the file matches the on-screen
    // figures. Formatting is generated server-side (same as the tracker report).
    const [exporting, setExporting] = useState(false);
    const handleExport = useCallback(async (tab) => {
        const yearMonth = toYearMonth(primaryMonth);
        if (exporting || !yearMonth)
            return;
        const payload = { tab, year_month: yearMonth, type: effectiveType };
        if (accountManager)
            payload.am_id = accountManager;
        if (tab !== "pacer") {
            payload.is_commission_client = commissionClientsOnly;
        }
        if (tab === "summary" && compareMonth)
            payload.compare_month = toYearMonth(compareMonth);
        setExporting(true);
        try {
            const response = await ApiCall("POST", "/api/commission-tracker/export-tabs", payload, { Authorization: token || "", "x-module-key": "commission_tracker" }, 7 * 60 * 1000, "blob");
            if (!response || response.status !== 200 || !response.data)
                throw new Error("Export failed");
            const url = window.URL.createObjectURL(response.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `commission_tracker_${tab}_${yearMonth}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        }
        catch (err) {
            console.error("Commission tracker export error:", err);
            toast.error("Failed to export. Please try again.");
        }
        finally {
            setExporting(false);
        }
    }, [exporting, primaryMonth, effectiveType, commissionClientsOnly, accountManager, compareMonth, token]);
    // Two-tone "Label: Value" text shown inside the header dropdown pills.
    const renderDropdownValue = (label, value) => (_jsxs("span", { children: [_jsxs("span", { style: { color: "#101828", fontWeight: 500 }, children: [label, ":"] }), " ", _jsx("span", { style: { color: "#667085" }, children: value })] }));
    // ✅ Loading State
    if (exporting) {
        return (_jsxs(Box, { sx: {
                position: "absolute",
                inset: 0,
                background: "white",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                zIndex: 100,
            }, children: [_jsx(CommonLoader, { className: "table-loader" }), exporting && (_jsx(Typography, { variant: "body1", color: "text.secondary", children: "Preparing your export. This may take a moment. It will download automatically once it's ready" }))] }));
    }
    return (_jsx(PageContainer, { children: _jsxs(Paper, { elevation: 0, sx: { display: "flex", flexDirection: "column", gap: "8px" }, children: [_jsxs(Box, { className: "pages-common-header-left", sx: {
                        width: "100%",
                        bgcolor: "#fff",
                        borderRadius: "8px",
                        border: "1px solid #D0D5DD",
                        px: 2.5,
                        py: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "nowrap",
                    }, children: [_jsx(Box, { sx: { flexShrink: 0 }, children: _jsx(LastUpdatedInfo, { updatedAt: lastUpdatedAt, onRefresh: handleRefreshAll, loading: refreshingAll, moduleName: "commission_tracker" }) }), _jsxs(Box, { sx: {
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "nowrap",
                                justifyContent: "flex-end",
                                flex: "1 1 auto",
                            }, children: [activeTab !== "pacer" && (_jsxs(Box, { sx: {
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 1,
                                        height: "32px",
                                        px: 1.25,
                                        border: "1px solid #D0D5DD",
                                        borderRadius: "8px",
                                        bgcolor: "#fff",
                                    }, children: [_jsx(Typography, { className: "all-Font-Sizes-12", sx: { fontSize: '13px !important', color: "#475467", fontWeight: 500, whiteSpace: "nowrap" }, children: "Commission Clients" }), _jsx(IOSSwitch, { checked: commissionClientsOnly, onChange: (e) => setCommissionClientsOnly(e.target.checked) })] })), canFilterByType && (_jsx(Dropdown, { value: type, onChange: (v) => setType(v), options: TYPE_OPTIONS, placeholder: "Type: All", displayValue: renderDropdownValue("Type", TYPE_OPTIONS.find((t) => t.value === type)?.label || "All"), leftIcon: "/assets/TypeC.svg", size: "sm", fullWidth: false, sortByLabel: false, className: "header-dropdown" })), _jsx(Dropdown, { value: accountManager, onChange: (v) => setAccountManager(v), options: amOptions, placeholder: "AM: All", displayValue: renderDropdownValue("AM", truncateName(selectedAm ? `${selectedAm.first_name} ${selectedAm.last_name}` : "All", 10)), leftIcon: "/assets/userC.svg", size: "sm", fullWidth: false, searchable: accountManagers.length > 10, className: "header-dropdown" }), _jsx(CustomMonthComparePicker, { value: primaryMonth, 
                                    // One compare month for both comparing tabs — applying it on
                                    // Summary has Pacer comparing the same months, and vice versa.
                                    compareValue: compareMonth, onApply: ({ main, compare }) => {
                                        setPrimaryMonth(main);
                                        setCompareMonth(compare);
                                    }, placeholder: "Select Month", comparePlaceholder: "Compare Month", comparison: activeTab === "summary" || activeTab === "pacer", futureMonths: 10 })] })] }), _jsx(Box, { sx: { bgcolor: "#fff", borderRadius: "8px", border: "1px solid #E5E7EB" }, children: _jsx(ChannelTabs, { tabs: visibleTabs, value: activeTab, onChange: setActiveTab }) }), activeTab === "daily" && (dailyLoading ? (_jsx(Box, { sx: { position: "absolute", inset: 0, background: "white", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : dailyError ? (_jsx(Alert, { severity: "error", sx: { borderRadius: "10px", alignItems: "center" }, children: dailyError })) : (_jsx(DailyUpdateTab, { rows: dailyRows, totals: dailyTotals, onExport: () => handleExport("daily"), exporting: exporting, canOpenReport: canOpenReport, asOfDate: dailyUpdate?.date, primaryMonth: toYearMonth(primaryMonth) }))), activeTab === "summary" && (summaryLoading ? (_jsx(Box, { sx: { position: "absolute", inset: 0, background: "white", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : summaryError ? (_jsx(Alert, { severity: "error", sx: { borderRadius: "10px", alignItems: "center" }, children: summaryError })) : (_jsx(SummaryTab, { rows: summaryRows, totals: summaryTotals, compareOn: !!compareMonth, onExport: () => handleExport("summary"), exporting: exporting, canOpenReport: canOpenReport, range: summary?.range, primaryMonth: toYearMonth(primaryMonth) }))), activeTab === "pacer" && (pacerLoading ? (_jsx(Box, { sx: { position: "absolute", inset: 0, background: "white", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : pacerError ? (_jsx(Alert, { severity: "error", sx: { borderRadius: "10px", alignItems: "center" }, children: pacerError })) : (_jsx(PacerTab, { pacer: pacer, onRefresh: handlePacerRefresh, refreshing: pacerLoading, onExport: () => handleExport("pacer"), exporting: exporting, onSaveNote: handleSavePacerNote })))] }) }));
}
